<template>
  <div class="custom-pricing-rules container-fluid">
    <z-overlay v-if="busy && !overlayHidden" notice="Working" custom-style="position: fixed;" @cancel="overlayHidden = true" />

    <div class="row mb-2">
      <div class="col">
        <h5 class="mb-1">Insurer Discount Rules</h5>
        <p class="text-muted mb-2">
          Set an insurer discount at a corporate location; every division and shop beneath it inherits the discount.
          Shops keep managing their own prices; the discount is applied on top when an estimate is priced.
        </p>
        <p class="text-muted small mb-0">
          <i class="fa-solid fa-circle-info me-1"></i>
          Applies to the shop's own pricing (per-insurer prices or "Our Pricing" defaults) and to insurer-mandated
          rates, where it replaces the insurer's own discount. Provider pricing is not affected.
        </p>
      </div>
    </div>

    <!-- Firm picker -->
    <div class="row mb-3">
      <div class="col-12 col-lg-6">
        <label class="form-label custom-label mb-0">Location</label>
        <Multiselect
          ref="firmPicker"
          v-model="selectedFirm"
          class="form-control custom-multiselect"
          placeholder="Search locations"
          :close-on-select="true"
          :filter-results="false"
          :min-chars="1"
          :resolve-on-load="false"
          :delay="400"
          :searchable="true"
          :options="async (query) => await fetchFirms(query)"
          label="firmName"
          value-prop="firm"
          :disabled="busy"
          @change="onFirmSelect"
        >
          <template #option="{ option }">
            {{ option.firmName }}
            <span class="badge bg-light text-muted border ms-1">{{ option.type }}</span>
          </template>
          <template #singlelabel="{ value }">
            <div class="multiselect-single-label">
              {{ value.firmName }}
              <span class="badge bg-light text-muted border ms-1">{{ value.type }}</span>
            </div>
          </template>
        </Multiselect>
      </div>
      <div class="col-12 col-lg-6 d-flex align-items-end justify-content-end">
        <button v-if="isCorporate" class="btn btn-primary btn-sm" :disabled="busy" @click="addRule">
          <i class="fa-solid fa-plus me-1"></i> Add Rule
        </button>
      </div>
    </div>

    <div v-if="!selectedFirm" class="alert alert-light border">
      Select a location to view and set its discount rules.
    </div>

    <template v-else>
      <div v-if="returnTo" class="mb-2">
        <button class="btn btn-link btn-sm p-0" :disabled="busy" @click="backToLocation">
          <i class="fa-solid fa-arrow-left me-1"></i> Back to {{ returnTo.firmName }}
        </button>
      </div>

      <div v-if="!isCorporate" class="alert alert-light border">
        Discounts are set at the corporate location. Below are the rules this location inherits.
      </div>

      <!-- Rules this firm owns (Corporate only) -->
      <div v-if="isCorporate" class="row mb-4">
        <div class="col">
          <h6 class="mb-2">Rules set at this location</h6>
          <table class="table table-sm table-hover align-middle">
            <thead>
              <tr>
                <th>Insurer</th>
                <th class="text-end">Discount</th>
                <th>Applies to</th>
                <th>Effective</th>
                <th class="text-end"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!rules.length">
                <td colspan="5" class="text-muted">
                  No rules set here. Shops beneath this location receive no corporate discount.
                </td>
              </tr>
              <tr v-for="r of rules" :key="r.pricingRule" :class="{ 'table-active': isPreviewing(r) }">
                <td>{{ r.insuranceName || `Insurer ${r.insurance}` }}</td>
                <td class="text-end">
                  <span :class="r.percent > 0 ? 'badge bg-primary' : 'badge bg-secondary'">{{ r.percent }}%</span>
                </td>
                <td>{{ scopeLabel(r.scope) }}</td>
                <td>{{ formatDate(r.effectiveOn) }}</td>
                <td class="text-end">
                  <button class="btn btn-link btn-sm p-0 me-3" :disabled="busy" @click="previewRule(r)">Preview</button>
                  <template v-if="!isDiscountRule(r)">
                    <button class="btn btn-link btn-sm p-0 me-3" :disabled="busy" @click="editRule(r)">Edit</button>
                    <button class="btn btn-link btn-sm p-0 text-danger" :disabled="busy" @click="removeRule(r)">Remove</button>
                  </template>
                  <span v-else class="text-muted small" title="Insurer-level discount; managed outside this page">Read-only</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- What this firm actually gets -->
      <div class="row" v-if="!isCorporate">
        <div class="col">
          <h6 class="mb-2">In effect at this location</h6>
          <p class="text-muted small">
            What an estimate at this location would use for its shop and insurer pricing when it is priced.
            Inherited rules are set at the corporate location.
          </p>
          <table class="table table-sm align-middle">
            <thead>
              <tr>
                <th>Insurer</th>
                <th class="text-end">Discount</th>
                <th>Applies to</th>
                <th>Source</th>
                <th class="text-end"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!effective.length">
                <td colspan="5" class="text-muted">No discount rules apply here.</td>
              </tr>
              <tr
                v-for="e of effective"
                :key="e.pricingRule"
                :class="{ 'text-muted': e.isInherited, 'table-active': isPreviewing(e) }"
              >
                <td>{{ e.insuranceName || `Insurer ${e.insurance}` }}</td>
                <td class="text-end">{{ e.percent }}%</td>
                <td>{{ scopeLabel(e.scope) }}</td>
                <td>
                  <span v-if="e.isInherited">
                    <i class="fa-solid fa-arrow-turn-up fa-rotate-90 me-1"></i>
                    inherited from
                    <button class="btn btn-link btn-sm p-0 align-baseline" :disabled="busy" @click="goToLocation(e)">
                      {{ e.firmName }}
                    </button>
                  </span>
                  <span v-else>set here</span>
                </td>
                <td class="text-end">
                  <button class="btn btn-link btn-sm p-0" :disabled="busy" @click="previewRule(e)">Preview</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Preview of one rule against a shop's own prices -->
      <div ref="preview">
        <pricing-rule-preview
          v-if="previewing"
          :rule="previewing"
          :firm="selectedFirm"
          :firm-type="selectedFirmType"
          @close="previewing = null"
        />
      </div>
    </template>

    <dialog-pricing-rule
      v-if="dialogOpen"
      :rule="editing"
      :firm="selectedFirm"
      :firm-name="selectedFirmName"
      @close="dialogOpen = false"
      @saved="onSaved"
    />
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { lookupStore } from '@/stores/lookup'
import { locationStore } from '@/stores/location'
import { pricingRuleStore } from '@/stores/pricingRule'
import Multiselect from '@vueform/multiselect'
import ZOverlay from '@/components/utilities/elements/Overlay.vue'
import DialogPricingRule from '@/components/pricing/DialogPricingRule.vue'
import PricingRulePreview from '@/components/pricing/PricingRulePreview.vue'
import { scopeLabel } from '@/components/pricing/scopeLabels'
import moment from 'moment'

export default {
  name: 'PricingRulesView',
  components: { Multiselect, ZOverlay, DialogPricingRule, PricingRulePreview },
  data() {
    return {
      selectedFirm: null,
      selectedFirmName: null,
      selectedFirmType: null,
      firmOptions: [],
      dialogOpen: false,
      editing: null,
      previewing: null,
      // The location we came from when following an inherited rule's source link.
      returnTo: null,
      overlayHidden: false
    }
  },
  computed: {
    ...mapState(authStore, ['profile']),
    ...mapState(pricingRuleStore, ['rules', 'effective', 'loading']),
    busy() {
      return this.loading.rules || this.loading.effective || this.loading.saving
    },
    // Rules can only be set at a Corporate firm (enforced by PricingRule_Save).
    isCorporate() {
      return this.selectedFirmType === 'Corporate'
    }
  },
  async created() {
    // Master insurer catalog (Firm 1) for the rule dialog's dropdown.
    await this.getLookups({ keys: ['Insurance'], firm: 1, refresh: false })
  },
  methods: {
    ...mapActions(lookupStore, ['getLookups']),
    ...mapActions(locationStore, ['searchLocations']),
    ...mapActions(pricingRuleStore, ['getRules', 'getEffectiveRules', 'deleteRule', 'reset']),
    async fetchFirms(query) {
      const results = await this.searchLocations({ searchterm: query, lookup: 5, firm: this.profile.firm })
      this.firmOptions = Array.isArray(results) ? results : []
      return this.firmOptions
    },
    async onFirmSelect(firm) {
      const match = this.firmOptions.find((f) => f.firm === firm)
      this.selectedFirmName = match ? match.firmName : null
      this.selectedFirmType = match ? match.type : null
      this.previewing = null
      // Any new selection drops the back link; goToLocation sets it again after selecting.
      this.returnTo = null
      if (!firm) {
        this.reset()
        return
      }
      await this.getRules(firm)
      await this.getEffectiveRules(firm)
    },
    addRule() {
      this.editing = null
      this.dialogOpen = true
    },
    editRule(rule) {
      if (this.isDiscountRule(rule)) return
      this.editing = rule
      this.dialogOpen = true
    },
    async removeRule(rule) {
      if (this.isDiscountRule(rule)) return
      const name = rule.insuranceName || `insurer ${rule.insurance}`
      if (!window.confirm(`Remove the ${rule.percent}% ${name} discount set at this location?\n\nShops beneath it will no longer receive this corporate discount for this insurer.`)) {
        return
      }
      await this.deleteRule(rule.pricingRule, this.selectedFirm)
      if (this.isPreviewing(rule)) {
        this.previewing = null
      }
    },
    async onSaved() {
      this.dialogOpen = false
      this.editing = null
      this.previewing = null
    },
    async previewRule(rule) {
      this.previewing = rule
      await this.$nextTick()
      this.$refs.preview?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    goToLocation(rule) {
      const from = { firm: this.selectedFirm, firmName: this.selectedFirmName, type: this.selectedFirmType }
      this.selectLocation({ firm: rule.firm, firmName: rule.firmName, type: 'Corporate' })
      this.returnTo = from
    },
    backToLocation() {
      this.selectLocation(this.returnTo)
    },
    // select() takes the option object, so its label renders even though the location
    // isn't in the current search results, and it fires @change -> onFirmSelect.
    selectLocation(option) {
      // onFirmSelect resolves the name and type from firmOptions, so the target must be in it.
      this.firmOptions = [option, ...this.firmOptions.filter((f) => f.firm !== option.firm)]
      this.$refs.firmPicker.select(option)
    },
    isPreviewing(rule) {
      return !!this.previewing && this.previewing.pricingRule === rule.pricingRule
    },
    scopeLabel,
    formatDate(value) {
      return value ? moment.parseZone(value).format('MM/DD/YYYY') : ''
    },
    // Old rules which shouldn't be editable
    isDiscountRule(rule) {
      return rule.ruleType === 'DiscountRule'
    }
  }
}
</script>
